import { graphql } from "babel-plugin-relay/macro";
import React from "react";
import { useFragment, useLazyLoadQuery } from "react-relay";
import { RightFrameContentSubmissionComponent_ContentSubmissionFragment$key } from "@generated/RightFrameContentSubmissionComponent_ContentSubmissionFragment.graphql";
import { RightFrameContentSubmissionComponent_LearnOpportunityFragment$key } from "@generated/RightFrameContentSubmissionComponent_LearnOpportunityFragment.graphql";
import { RightFrameContentSubmissionComponent_NodeQuery } from "@generated/RightFrameContentSubmissionComponent_NodeQuery.graphql";
import { RightFrameContentSubmissionComponent_Query } from "@generated/RightFrameContentSubmissionComponent_Query.graphql";
import { NoAuthorizedContentScreen } from "./NoAuthorizedContentScreen";
import { RightFrameContentElement } from "./RightFrameContentElement";
import { selectNoAuthorizedMessage } from "../../../infecto-lms-webapp/utils/selectNoAuthorizedMessage";
import { ContentSubmissionEnd } from "../content-submission-end";

const QUERY = graphql`
	query RightFrameContentSubmissionComponent_Query($contentNodeId: ID!) {
		ContentSubmission {
			UserLastContentSubmission(contentNodeId: $contentNodeId) {
				... on ContentSubmission {
					...RightFrameContentSubmissionComponent_ContentSubmissionFragment
				}
			}
		}
		Viewer {
			Auth {
				currentUser {
					user {
						extension(userExtensionKind: InfectopharmUserExtension) {
							... on InfectopharmUserExtension {
								globalUserStatus
							}
						}
					}
				}
			}
		}
	}
`;

const CONTENT_SUBMISSION_FRAGMENT = graphql`
	fragment RightFrameContentSubmissionComponent_ContentSubmissionFragment on ContentSubmission {
		id
		learnOpportunity {
			id
		}
		definition {
			status
		}
		...RightFrameContentElement_ContentSubmissionFragment
		...contentSubmissionEnd_ContentSubmissionFragment
	}
`;

const NODE_QUERY = graphql`
	query RightFrameContentSubmissionComponent_NodeQuery($learnOpportunityId: ID!) {
		node(id: $learnOpportunityId) {
			id
			... on LearnOpportunityV2 {
				id
				...RightFrameContentSubmissionComponent_LearnOpportunityFragment
			}
		}
	}
`;

const NODE_FRAGMENT = graphql`
	fragment RightFrameContentSubmissionComponent_LearnOpportunityFragment on LearnOpportunityV2 {
		id
		root {
			id
			structureDefinition {
				title
				... on LearnOpportunityRootStructureDefinition {
					viewerTreeState {
						kind

						... on CanNotBeStartedViewerTreeState {
							reasonConfigTypes
						}
						... on IsStartedViewerTreeState {
							headContentId
						}
					}
				}
			}
		}
		shortDescription
		structureDefinition {
			title
		}
		typeDefinition {
			... on LearnOpportunityELearningContentTypeDefinition {
				definitionType
				contentNodeAdvancementResult {
					status
					... on CanNotBeStartedContentNodeAdvancementResult {
						reasonConfigTypes
					}
					... on ContinueContentNodeAdvancementResult {
						activeContentSubmissionId
					}
					... on CanNotBeRestartedAfterFailedContentNodeAdvancementResult {
						status
						configResults {
							configType

							... on RestartIfFailedContentConfigResult {
								canBeRestarted
							}

							... on NegativeBlockPermanentlyWhenRewardedRestartIfFailedContentConfigResult {
								canBeRestarted
							}

							... on NegativeBlockTemporarilyWhenRewardedRestartIfFailedContentConfigResult {
								blockedUntil
							}
						}
					}
				}
				containedElementTypes
			}
		}
	}
`;
interface OwnProps {
	contentNodeId: string;
	setIsModuleExpiredAndFailed: (v: boolean) => void;
	scrollRef?: React.RefObject<HTMLDivElement>;
	scrollOnReady?: boolean;
}
export const RightFrameContentSubmissionComponent = ({
	contentNodeId,
	setIsModuleExpiredAndFailed,
	scrollRef,
	scrollOnReady,
}: OwnProps) => {
	const data = useLazyLoadQuery<RightFrameContentSubmissionComponent_Query>(
		QUERY,
		{
			contentNodeId: contentNodeId!,
		},
		{ fetchPolicy: "network-only" },
	);

	const contentSubmission =
		useFragment<RightFrameContentSubmissionComponent_ContentSubmissionFragment$key>(
			CONTENT_SUBMISSION_FRAGMENT,
			data.ContentSubmission.UserLastContentSubmission,
		);

	const nodeQuery = useLazyLoadQuery<RightFrameContentSubmissionComponent_NodeQuery>(NODE_QUERY, {
		learnOpportunityId: contentNodeId!,
	});

	const learnOpportunity =
		useFragment<RightFrameContentSubmissionComponent_LearnOpportunityFragment$key>(
			NODE_FRAGMENT,
			nodeQuery.node!,
		);

	const startContentConfig =
		learnOpportunity?.typeDefinition?.contentNodeAdvancementResult?.reasonConfigTypes?.includes(
			"StartContent_SpecificUserStatusesCan",
		);
	const startTreeConfig =
		learnOpportunity?.root?.structureDefinition.viewerTreeState?.reasonConfigTypes?.includes(
			"StartTree_SpecificUserRolesCan",
		);

	return contentSubmission &&
		contentSubmission.learnOpportunity &&
		!startContentConfig &&
		!startTreeConfig ? (
		<>
			{contentSubmission.definition.status !== "active" && (
				<ContentSubmissionEnd
					setIsModuleExpiredAndFailed={setIsModuleExpiredAndFailed}
					contentSubmissionFragmentRef={contentSubmission}
				/>
			)}
			{contentSubmission.definition.status === "active" && (
				<RightFrameContentElement
					scrollOnReady={scrollOnReady}
					contentSubmissionFragmentRef={contentSubmission}
					scrollRef={scrollRef}
				/>
			)}
		</>
	) : (
		<NoAuthorizedContentScreen
			title={selectNoAuthorizedMessage(
				learnOpportunity?.root?.structureDefinition.viewerTreeState?.reasonConfigTypes,
				data.Viewer.Auth.currentUser?.user.extension?.globalUserStatus,
			)}
		/>
	);
};
